import { create } from "zustand";
import { HeatmapLayer } from "@deck.gl/aggregation-layers";
import PaletteService from "../services/palette.service";
import GeodesicService from "../services/geodesic.service";
import { getLayerForTime, getPalette } from "../services/api/heatmap.service";
import useMapState from "./MapState";
import { usePlayingState } from "./PlayingState";
import { getVariables } from "../services/variables.service";
import { useParticlesLayersState } from "./ParticlesLayersState";

const HISTOGRAM_THRESHOLD = 8;
const MIN_THRESHOLDS = {
  WAVE: 15,
  SEA_LEVEL: 20,
  SALINITY: 8,
  CURRENTS: 10,
  WATER_TEMP: 8,
  WIND: 8,
};

export const useHeatmapLayersState = create((set, get) => ({
  domains: [],
  variable: null,
  latLonStep: { latStep: null, lonStep: null },
  paletteMinMax: { min: null, max: null },
  paletteHistogram: [],
  paletteDistribution: [100],
  colorDomain: [0, 100],

  heatmapRequestId: 0,
  setHeatmapRequestId: (id) => set({ heatmapRequestId: id }),

  paletteRequestId: 0,
  setPaletteRequestId: (id) => set({ paletteRequestId: id }),
  paletteController: null,
  setPaletteController: (controller) => set({ paletteController: controller }),

  setDomains: (domains) => set({ domains }),
  setVariable: (variable) => set({ variable }),
  setLatLonStep: (latLonStep) => set({ latLonStep }),
  setPaletteMinMax: (paletteMinMax) => set({ paletteMinMax }),
  setPaletteHistogram: (paletteHistogram) => set({ paletteHistogram }),
  setPaletteDistribution: (paletteDistribution) => set({ paletteDistribution }),
  setColorDomain: (colorDomain) => set({ colorDomain }),

  setPalette: async () => {
    if (!get().variable) {
      console.error("Variable is not defined");
      return;
    }
    console.log("setPalette: entra");
    const { dateFrom, dateTo } = usePlayingState.getState();

    // Genera un identificador único para la solicitud actual y asigna un AbortController.
    const currentPaletteRequestId = Date.now();
    get().setPaletteRequestId(currentPaletteRequestId);
    const paletteController = new AbortController();
    get().setPaletteController(paletteController);

    try {
      const { min, max, histogram } = await getPalette(
        get().domains,
        dateFrom,
        dateTo,
        getVariables(get().variable),
        paletteController.signal // Se pasa el signal para permitir la cancelación
      );

      // Doble chequeo: si el requestId ha cambiado, se descarta la respuesta.
      if (get().paletteRequestId !== currentPaletteRequestId) {
        console.log("Respuesta obsoleta de palette, ignorando...");
        return;
      }

      get().setPaletteMinMax({ min, max });
      console.log("setPaletteMinMax", min, max);
      console.log("histogram: ", histogram);

      get().setPaletteHistogram(histogram);
      get().setPaletteDistribution(histogram.map((d) => d.value));

      let colorDomain = PaletteService.getColorDomain(
        get().paletteDistribution,
        HISTOGRAM_THRESHOLD,
        MIN_THRESHOLDS[get().variable.toUpperCase()]
      );
      console.log("colorDomain: ", colorDomain);
      get().setColorDomain(colorDomain);
    } catch (err) {
      if (err.name === "AbortError") {
        console.log("Palette request canceled");
      } else {
        console.log("Error getPalette");
      }
    }
  },

  getLayersForTime: async (date) => {
    // Generamos un identificador único para la solicitud actual.
    const currentRequestId = Date.now();
    get().setHeatmapRequestId(currentRequestId);
    console.log("getLayersForTime: entra", currentRequestId);

    const mapState = useMapState.getState();
    let reduceOpacity = useParticlesLayersState.getState().domains.length;
    const newLayers = [];

    for (let domain of get().domains) {
      if (!domain.controller) {
        domain.controller = new AbortController();
      }
      const { signal } = domain.controller;
      try {
        const { data, latStep, lonStep } = await getLayerForTime(
          date,
          domain,
          get().paletteMinMax.min,
          get().paletteMinMax.max,
          getVariables(get().variable),
          mapState.viewState.zoom,
          signal
        );
        // Doble chequeo: si el requestId ha cambiado, descartamos la respuesta.
        if (get().heatmapRequestId !== currentRequestId) {
          console.log(
            `Respuesta obsoleta para domain ${domain.id}, ignorando...`
          );
          continue;
        }
        get().setLatLonStep({ latStep, lonStep });
        const heatmapLayer = new HeatmapLayer({
          data,
          opacity: reduceOpacity ? 0.4 : 1,
          id: `heatmap-layer-${domain.id}`,
          getPosition: (d) => [d[0], d[1]],
          getWeight: (d) => d[2],
          aggregation: "MEAN",
          radiusPixels: GeodesicService.getRadiusPixel(
            get().latLonStep.latStep,
            get().latLonStep.lonStep,
            {
              viewE: domain.viewE,
              viewS: domain.viewS,
              viewN: domain.viewN,
              viewW: domain.viewW,
            }
          ),
          intensity: 1,
          colorRange: PaletteService.getColorsDistribution(
            "temperature",
            get().paletteDistribution,
            true
          ),
          colorDomain: get().colorDomain,
          userData: {
            option: domain.option,
            zIndex: 1,
          },
        });
        newLayers.push(heatmapLayer);
      } catch (error) {
        if (error.name === "AbortError") {
          console.log(`Request for domain ${domain.id} was canceled.`);
        } else {
          console.error("Error fetching data:", error);
        }
      }
    }
    mapState.addOrUpdateLayers(newLayers);
  },

  refreshLayers: async () => {
    await get().setPalette();
    const { timeInterval, timeIndex } = usePlayingState.getState();
    let time = timeInterval[timeIndex];
    get().getLayersForTime(time);
  },

  addDomains: async (newDomains) => {
    const mapState = useMapState.getState();

    // Añadir AbortController a los nuevos dominios
    const domainsWithControllers = newDomains.map((domain) => ({
      ...domain,
      controller: new AbortController(),
    }));

    get().setDomains([...get().domains, ...domainsWithControllers]);

    // Si la reproducción ya empezó pero está en paused, cargamos el layer actual desde aquí
    if (usePlayingState.getState().paused && get().domains.length) {
      await get().refreshLayers();
    }
  },

  removeDomains: (optionId) => {
    const mapState = useMapState.getState();
    const newDomains = get().domains.filter((d) => d.option.id !== optionId);
    const removedDomains = get().domains.filter(
      (d) => d.option.id === optionId
    );

    // Cancelar solicitudes en vuelo de los dominios eliminados
    removedDomains.forEach((domain) => {
      if (domain.controller) {
        console.log("Domain Abortando: ", domain.id);
        domain.controller.abort();
      }
    });

    if (newDomains.length !== get().domains.length) {
      get().setDomains(newDomains);
      mapState.removeLayers(optionId);
      if (usePlayingState.getState().paused && get().domains.length) {
        get().refreshLayers();
      }
    }
    if (newDomains.length === 0) {
      get().setVariable(null);
    }
  },
}));

window.store = useHeatmapLayersState;
